import { useState, useMemo } from 'react';
import { FileText, Download, AlertTriangle } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLogger } from '../hooks/useLoggers';
import { useProbes } from '../hooks/useProbes';
import { useMeasurementsCombined } from '../hooks/useMeasurementsCombined';
import type { Probe } from '../services/data';
import Card from '../components/ui/Card';
import Spinner from '../components/ui/Spinner';
import StatusBadge from '../components/ui/StatusBadge';
import Button from '../components/ui/Button';
import ProbeChart from '../components/charts/ProbeChart';
import ProbeTabs from '../components/logger/ProbeTabs';
import PeriodSelector, { makeDefaultPeriod, type Period } from '../components/logger/PeriodSelector';
import ProbeStats from '../components/logger/ProbeStats';
import api from '../services/api';

import { computeMeasurementStats } from '../utils/statsEngine';

export default function LoggerDetail() {
  const { t } = useTranslation();
  const { loggerId } = useParams<{ loggerId: string }>();
  const [selectedProbeIndex, setSelectedProbeIndex] = useState(0);
  const [period, setPeriod] = useState<Period>(makeDefaultPeriod);
  const [isExporting, setIsExporting] = useState(false);

  const { data: logger, isLoading: loggerLoading, error: loggerError } = useLogger(loggerId);
  const { data: probes = [], isLoading: probesLoading } = useProbes(loggerId);

  if (loggerLoading || probesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (loggerError || !logger) {
    return (
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-6">{t('common.error')}</h1>
        <Card>
          <p className="text-red-600">{t('common.error')}</p>
        </Card>
      </div>
    );
  }

  const getLoggerStatus = () => {
    if (!logger.is_active) return 'offline';
    const lastSeenDate = logger.last_seen_at ? new Date(logger.last_seen_at) : null;
    if (!lastSeenDate) return 'unknown';
    const hoursSinceLastSeen = (Date.now() - lastSeenDate.getTime()) / (1000 * 60 * 60);
    if (hoursSinceLastSeen > 24) return 'offline';
    if (hoursSinceLastSeen > 2) return 'warning';
    return 'ok';
  };

  const formatLastSeen = (lastSeen?: string) => {
    if (!lastSeen) return t('common.never');
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffMins < 60) return `${diffMins} min`;
    if (diffHours < 24) return `${diffHours}h`;
    return `${diffDays}j`;
  };

  const handleExport = async (format: 'pdf' | 'csv') => {
    if (!loggerId || !probes[selectedProbeIndex]) return;
    setIsExporting(true);
    try {
      const probe = probes[selectedProbeIndex];
      const resp = await api.get(`/loggers/${loggerId}/report/${format}`, {
        params: { probe_id: probe.id, start: period.start, end: period.end, tz_offset: -new Date().getTimezoneOffset() },
        responseType: 'blob',
      });
      const blob = new Blob([resp.data], {
        type: format === 'pdf' ? 'application/pdf' : 'text/csv',
      });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      const now = new Date();
      const yyyymmdd = now.toISOString().slice(0, 10).replace(/-/g, '');
      const hhmm = now.toTimeString().slice(0, 5).replace(':', '');
      const slot = `P${String((probe.position ?? 1)).padStart(2, '0')}`;
      const periodLabel = period.preset === '7d' ? '7j' : period.preset === '30d' ? '30j' : period.preset ?? 'custom';
      a.download = `${logger.serial_number}_${slot}_${yyyymmdd}_${hhmm}_${periodLabel}.${format}`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error('Export error:', e);
    } finally {
      setIsExporting(false);
    }
  };

  const selectedProbe = probes[selectedProbeIndex] as Probe | undefined;

  return (
    <div>
      {/* Back button */}
      <div className="mb-4">
        <Button variant="secondary" size="sm" onClick={() => window.history.back()}>
          ← {t('common.backToList')}
        </Button>
      </div>

      {/* Logger header */}
      <Card className="mb-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{logger.name}</h1>
            <p className="text-sm text-gray-500 mt-0.5">{logger.serial_number}</p>
          </div>
          <StatusBadge status={getLoggerStatus()} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          {logger.battery_level != null && (
            <div>
              <p className="text-xs text-gray-500">{t('logger.battery')}</p>
              <p className="text-sm font-semibold">{logger.battery_level}%</p>
              <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                <div
                  className={`h-1.5 rounded-full ${
                    logger.battery_level > 50 ? 'bg-green-500' : logger.battery_level > 20 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${logger.battery_level}%` }}
                />
              </div>
            </div>
          )}
          <div>
            <p className="text-xs text-gray-500">{t('logger.lastSeen')}</p>
            <p className="text-sm font-semibold">{formatLastSeen(logger.last_seen_at)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">{t('logger.probes')}</p>
            <p className="text-sm font-semibold">
              {probes.length} {probes.length > 1 ? t('logger.probesPlural') : t('logger.probesSingular')}
            </p>
          </div>
        </div>
      </Card>

      {probes.length === 0 ? (
        <Card>
          <p className="text-gray-500 text-center py-8">{t('logger.noProbes')}</p>
        </Card>
      ) : (
        <Card className="!p-0">
          {/* Probe tabs */}
          <ProbeTabs
            probes={probes}
            selectedIndex={selectedProbeIndex}
            onChange={setSelectedProbeIndex}
          />

          {/* Probe content */}
          {selectedProbe && (
            <div className="p-4 space-y-4">
              <ProbeContent
                probe={selectedProbe}
                loggerId={loggerId!}
                period={period}
                onPeriodChange={setPeriod}
                onExport={handleExport}
                isExporting={isExporting}
              />
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// ─── ExportButton ─────────────────────────────────────────────────────────────

interface ExportButtonProps {
  label: string;
  format: 'pdf' | 'csv';
  onClick: () => void;
  disabled: boolean;
  hasGap: boolean;
  tooltip: string;
  gapTooltip: string;
}

function ExportButton({ label, format, onClick, disabled, hasGap, tooltip, gapTooltip }: ExportButtonProps) {
  const isPdf = format === 'pdf';
  const Icon = isPdf ? FileText : Download;
  const colorClass = isPdf
    ? 'bg-red-600 hover:bg-red-700 text-white'
    : 'bg-emerald-600 hover:bg-emerald-700 text-white';

  return (
    <div className="relative group">
      <button
        onClick={onClick}
        disabled={disabled}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${colorClass}`}
      >
        <Icon size={15} />
        {label}
        {hasGap && (
          <AlertTriangle size={13} className="text-amber-300 ml-0.5" />
        )}
      </button>
      {/* Tooltip */}
      <div className="absolute bottom-full mb-2 right-0 z-10 hidden group-hover:block w-64 pointer-events-none">
        <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg">
          <p>{tooltip}</p>
          {hasGap && (
            <p className="mt-1 text-amber-300 flex items-start gap-1">
              <AlertTriangle size={11} className="shrink-0 mt-0.5" />
              {gapTooltip}
            </p>
          )}
          {/* Arrow */}
          <div className="absolute top-full right-4 border-4 border-transparent border-t-gray-900" />
        </div>
      </div>
    </div>
  );
}

// ─── ProbeContent ────────────────────────────────────────────────────────────

interface ProbeContentProps {
  probe: Probe;
  loggerId: string;
  period: Period;
  onPeriodChange: (p: Period) => void;
  onExport: (format: 'pdf' | 'csv') => void;
  isExporting: boolean;
}

function ProbeContent({ probe, period, onPeriodChange, onExport, isExporting }: ProbeContentProps) {
  const { t } = useTranslation();

  const { data: combined, isLoading } = useMeasurementsCombined(probe.id, period);

  const official = combined?.official ?? [];
  const adv = combined?.adv ?? [];
  const statsOfficial = combined?.statsOfficial ?? null;

  // ADV hidden if range > 7 days
  const diffMs = new Date(period.end).getTime() - new Date(period.start).getTime();
  const hideAdv = diffMs > 7 * 24 * 60 * 60 * 1000;

  // Measurements in ascending order for deviation calculation
  const officialAsc = [...official].reverse();

  // Compute avg interval from official data (statsEngine)
  const engineStats = officialAsc.length > 1
    ? computeMeasurementStats(
        officialAsc.map((m) => Number(m.value_calibrated)),
        officialAsc.map((m) => new Date(m.timestamp).getTime() / 1000),
      )
    : null;

  const officialChartData = useMemo(() => official.map((m) => ({
    timestamp: m.timestamp,
    value: Number(m.value_calibrated),
  })), [official]);
  const advChartData = useMemo(() => adv.map((m) => ({
    timestamp: m.timestamp,
    value: Number(m.value_calibrated),
  })), [adv]);
  const thresholds = useMemo(() => ({
    alarmLow: probe.alarm_low,
    alarmHigh: probe.alarm_high,
    warningLow: probe.warning_low,
    warningHigh: probe.warning_high,
  }), [probe.alarm_low, probe.alarm_high, probe.warning_low, probe.warning_high]);

  // Latest value
  const latestValue =
    official.length > 0
      ? Number(official[0].value_calibrated)
      : adv.length > 0
      ? Number(adv[0].value_calibrated)
      : undefined;

  // Check if official data covers the full requested period.
  // official is DESC: [0] = most recent, [last] = oldest.
  // Allow a gap of 2× avg interval at each end (normal sync jitter).
  const hasOfficialGap = useMemo(() => {
    if (official.length === 0) return true;
    const intervalMs = engineStats?.avg_interval_minutes
      ? engineStats.avg_interval_minutes * 2 * 60 * 1000
      : 2 * 60 * 60 * 1000; // default 2h tolerance if interval unknown
    const periodStartMs = new Date(period.start).getTime();
    const periodEndMs = new Date(period.end).getTime();
    const oldestMs = new Date(official[official.length - 1].timestamp).getTime();
    const newestMs = new Date(official[0].timestamp).getTime();
    return oldestMs > periodStartMs + intervalMs || newestMs < periodEndMs - intervalMs;
  }, [official, period, engineStats]);

  return (
    <>
      {/* Probe header row */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{probe.name}</h2>
          <p className="text-sm text-gray-500 capitalize">{probe.measurement_type}</p>
        </div>
        {latestValue !== undefined && (
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-900">
              {latestValue.toFixed(1)}{probe.unit}
            </p>
            <p className="text-xs text-gray-500">{t('logger.currentValue')}</p>
          </div>
        )}
      </div>

      {/* Period selector + Export buttons */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <PeriodSelector value={period} onChange={onPeriodChange} />
        </div>
        <div className="flex gap-2 shrink-0 mt-0.5">
          <ExportButton
            label={t('logger.exportPdf')}
            format="pdf"
            onClick={() => onExport('pdf')}
            disabled={isExporting}
            hasGap={hasOfficialGap}
            gapTooltip={t('logger.exportGapWarning')}
            tooltip={t('logger.exportPdfTooltip')}
          />
          <ExportButton
            label={t('logger.exportCsv')}
            format="csv"
            onClick={() => onExport('csv')}
            disabled={isExporting}
            hasGap={hasOfficialGap}
            gapTooltip={t('logger.exportGapWarning')}
            tooltip={t('logger.exportCsvTooltip')}
          />
        </div>
      </div>

      {/* Chart */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Spinner size="lg" />
        </div>
      ) : (
        <ProbeChart
          officialData={officialChartData}
          advData={advChartData}
          unit={probe.unit}
          thresholds={thresholds}
          hideAdv={hideAdv}
          periodStart={period.start}
          periodEnd={period.end}
        />
      )}

      {/* Stats */}
      <ProbeStats
        stats={statsOfficial}
        measurements={officialAsc}
        unit={probe.unit}
        thresholds={{
          alarmLow: probe.alarm_low,
          alarmHigh: probe.alarm_high,
        }}
        avgIntervalMinutes={engineStats?.avg_interval_minutes}
      />
    </>
  );
}
